import { useState, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { Tag, Tooltip } from '@cimpress/react-components';
import styles from './TagInput.module.scss';
import IconArrowDownAlt from '@cimpress-technology/react-streamline-icons/lib/IconArrowDownAlt';

interface TagInputProps {
  label?: string;
  value: string[];
  onChange: (tags: string[]) => void;
}

const TagInput = ({ label = 'Tags', value, onChange }: TagInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isFloating = isFocused || value.length > 0 || inputValue.length > 0;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && inputValue.trim()) {
      e.preventDefault();
      const newTag = inputValue.trim();

      if (!value.includes(newTag)) {
        onChange([...value, newTag]);
      }

      setInputValue('');
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      onChange(value.slice(0, -1));
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onChange(value.filter((tag) => tag !== tagToRemove));
  };

  const handleBlur = () => {
    if (inputValue.trim() && !value.includes(inputValue.trim())) {
      onChange([...value, inputValue.trim()]);
      setInputValue('');
    }
    setIsFocused(false);
  };

  return (
    <div className={styles.tagInputWrapper}>
      <div
        className={`${styles.tagInputContainer} ${isFocused ? styles.focused : ''}`}
        onClick={() => inputRef.current?.focus()}
      >
        <label
          className={`${styles.floatingLabel} ${isFloating ? styles.floated : ''}`}
        >
          {label}
        </label>

        <div className={styles.tagsWrapper}>
          {value.map((tag) => (
            <Tag
              key={tag}
              value={tag}
              removable
              onRemoveClick={() => handleRemoveTag(tag)}
              className={styles.tag}
            />
          ))}

          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
          />
        </div>
        {value.length > 0 && (
          <Tooltip
            direction="bottom"
            variant="popover"
            contents={
              <div className={styles.tooltipContent}>
                {value.map((tag) => (
                  <Tag
                    key={`tooltip-${tag}`}
                    value={tag}
                    removable
                    onRemoveClick={() => handleRemoveTag(tag)}
                  />
                ))}
              </div>
            }
          >
            <div className={styles.countIndicator}>
              <IconArrowDownAlt color="#008080" size="lg"></IconArrowDownAlt>
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default TagInput;
